// Athletes & coaches of a company, shown on the company profile page (see
// `features/athletes.md`). Coaches and athletes are managed as separate lists
// — two tabs over the same underlying collection, split on the coach mark —
// so a company handles its coaching staff apart from its athletes. Reading
// comes with company access; creating, editing and deleting are gated by
// MANAGE_ATHLETES (the server enforces the same rules on every request).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import type { Athlete, Sport, SportProfile } from '../domain';
import { athleteAge } from '../domain';
import {
  deleteAthlete,
  fetchAthletes,
  fetchSports,
  fetchSportProfiles,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { AthleteDialog } from './AthleteDialog';
import { SportProfileDialog } from './SportProfileDialog';

type RosterTab = 'athletes' | 'coaches';

interface AthletesSectionProps {
  companyId: number;
  /** Open an athlete's full profile page (photos, club, teams, training). */
  onOpenProfile: (personalNumber: string) => void;
}

export function AthletesSection({ companyId, onOpenProfile }: AthletesSectionProps) {
  const { hasPermission } = useAuth();
  const { t, localeCode } = useTranslation();
  const canManage = hasPermission('MANAGE_ATHLETES');

  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [profiles, setProfiles] = useState<SportProfile[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<RosterTab>('athletes');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Athlete | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Athlete | null>(null);
  // The sport-profile dialog's target: which athlete, and which of their
  // profiles (null = create a new one).
  const [profileDialog, setProfileDialog] = useState<{
    athlete: Athlete;
    profile: SportProfile | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  // Read the translator through a ref so the loader's identity is stable
  // across language switches (same pattern as the users loader on this page).
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [roster, sportProfiles, catalog] = await Promise.all([
        fetchAthletes(companyId),
        fetchSportProfiles(companyId),
        fetchSports(),
      ]);
      setAthletes(roster);
      setProfiles(sportProfiles);
      setSports(catalog);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.athletes.errors.load'),
      );
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Coaches and athletes are managed separately: one collection, split on the
  // coach mark.
  const coaches = useMemo(() => athletes.filter((a) => a.isCoach), [athletes]);
  const plain = useMemo(() => athletes.filter((a) => !a.isCoach), [athletes]);
  const visible = tab === 'coaches' ? coaches : plain;

  // Sport profiles per athlete. Managers edit them through the profile
  // dialog (chip click / the "+" chip); everyone else sees read-only chips.
  const profilesByAthlete = useMemo(() => {
    const map = new Map<string, SportProfile[]>();
    for (const profile of profiles) {
      const list = map.get(profile.athleteId) ?? [];
      list.push(profile);
      map.set(profile.athleteId, list);
    }
    return map;
  }, [profiles]);

  // Prefer the locale catalogue entry for a sport name; unknown sports fall
  // back to the catalog's English name, then to the code.
  const sportName = (sportId: string) => {
    const translated = t(`sports.${sportId}`);
    if (translated !== `sports.${sportId}`) return translated;
    return sports.find((s) => s.id === sportId)?.name ?? sportId;
  };

  // A compact "grip: SHAKEHAND · formats: SINGLES, DOUBLES" summary for the
  // profile chip's tooltip. Attribute values are catalog codes, shown as-is.
  const attributeSummary = (profile: SportProfile) =>
    Object.entries(profile.attributes)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
      .join(' · ');

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium', timeZone: 'UTC' }),
    [localeCode],
  );

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setBusy(true);
    setError(null);
    try {
      setAthletes(await deleteAthlete(target.personalNumber));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athletes.errors.delete'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('admin.athletes.title')}
        </Typography>
        {canManage && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={busy}
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            {t(tab === 'coaches' ? 'admin.athletes.addCoach' : 'admin.athletes.addAthlete')}
          </Button>
        )}
      </Stack>

      <Tabs value={tab} onChange={(_, value: RosterTab) => setTab(value)} sx={{ mb: 2 }}>
        <Tab
          value="athletes"
          label={t('admin.athletes.tabs.athletes', { count: plain.length })}
        />
        <Tab
          value="coaches"
          label={t('admin.athletes.tabs.coaches', { count: coaches.length })}
        />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(
            tab === 'coaches'
              ? canManage
                ? 'admin.athletes.emptyCoachesEditable'
                : 'admin.athletes.emptyCoaches'
              : canManage
                ? 'admin.athletes.emptyAthletesEditable'
                : 'admin.athletes.emptyAthletes',
          )}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {visible.map((athlete) => {
            const age = athleteAge(athlete.dateOfBirth);
            return (
              <Paper
                key={athlete.personalNumber}
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <Avatar
                  src={athlete.photoUrls[0] ?? undefined}
                  alt={athlete.fullName}
                  sx={{ width: 48, height: 48 }}
                >
                  {athlete.isCoach ? <SportsIcon /> : <DirectionsRunIcon />}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="subtitle1"
                      noWrap
                      title={athlete.fullName}
                      onClick={() => onOpenProfile(athlete.personalNumber)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {athlete.fullName}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={athlete.personalNumber}
                      sx={{ fontFamily: 'monospace' }}
                    />
                    {athlete.isCoach && (
                      <Chip
                        size="small"
                        color="secondary"
                        label={t('admin.athletes.coachChip')}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {t(`admin.athletes.gender.${athlete.gender}`)}
                    {' · '}
                    {t('admin.athletes.born', {
                      date: dateFormat.format(
                        new Date(`${athlete.dateOfBirth}T00:00:00Z`),
                      ),
                    })}
                    {age !== null &&
                      ` · ${t('admin.athletes.age', { count: age })}`}
                  </Typography>
                  {((profilesByAthlete.get(athlete.personalNumber) ?? []).length > 0 ||
                    canManage) && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.5 }}
                    >
                      {(profilesByAthlete.get(athlete.personalNumber) ?? []).map(
                        (profile) => (
                          <Tooltip key={profile.id} title={attributeSummary(profile)}>
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              label={sportName(profile.sportId)}
                              aria-label={
                                canManage
                                  ? t('admin.athletes.profiles.editAria', {
                                      sport: sportName(profile.sportId),
                                      name: athlete.fullName,
                                    })
                                  : undefined
                              }
                              onClick={
                                canManage
                                  ? () => setProfileDialog({ athlete, profile })
                                  : undefined
                              }
                            />
                          </Tooltip>
                        ),
                      )}
                      {canManage && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<AddIcon />}
                          label={t('admin.athletes.profiles.add')}
                          aria-label={t('admin.athletes.profiles.addAria', {
                            name: athlete.fullName,
                          })}
                          disabled={busy}
                          onClick={() => setProfileDialog({ athlete, profile: null })}
                        />
                      )}
                    </Stack>
                  )}
                  {athlete.isCoach && athlete.coachDescription && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {athlete.coachDescription}
                    </Typography>
                  )}
                </Box>
                <Tooltip
                  title={t('admin.athletes.viewProfileAria', { name: athlete.fullName })}
                >
                  <span>
                    <IconButton
                      aria-label={t('admin.athletes.viewProfileAria', {
                        name: athlete.fullName,
                      })}
                      disabled={busy}
                      onClick={() => onOpenProfile(athlete.personalNumber)}
                    >
                      <AccountBoxIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {canManage && (
                  <Tooltip
                    title={t('admin.athletes.editAria', { name: athlete.fullName })}
                  >
                    <span>
                      <IconButton
                        aria-label={t('admin.athletes.editAria', {
                          name: athlete.fullName,
                        })}
                        disabled={busy}
                        onClick={() => {
                          setEditing(athlete);
                          setDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canManage && (
                  <Tooltip
                    title={t('admin.athletes.deleteAria', { name: athlete.fullName })}
                  >
                    <span>
                      <IconButton
                        aria-label={t('admin.athletes.deleteAria', {
                          name: athlete.fullName,
                        })}
                        disabled={busy}
                        onClick={() => setPendingDelete(athlete)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Separate dialogs for coaches and athletes: the role comes from the
          Add button's tab when creating, and from the profile itself when
          editing — it is never toggled inside the dialog. */}
      <AthleteDialog
        open={dialogOpen}
        companyId={companyId}
        athlete={editing}
        isCoach={editing ? editing.isCoach : tab === 'coaches'}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void load()}
      />

      {/* Sport-profile dialog: mutations respond with the company's refreshed
          profile list, so no reload is needed. */}
      {profileDialog && (
        <SportProfileDialog
          open={profileDialog !== null}
          athlete={profileDialog.athlete}
          profile={profileDialog.profile}
          sports={sports}
          existingSportIds={(
            profilesByAthlete.get(profileDialog.athlete.personalNumber) ?? []
          ).map((profile) => profile.sportId)}
          onClose={() => setProfileDialog(null)}
          onSaved={setProfiles}
        />
      )}

      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>{t('admin.athletes.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDelete
              ? t('admin.athletes.deleteDialog.body', {
                  name: pendingDelete.fullName,
                  number: pendingDelete.personalNumber,
                })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmDelete()}>
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
